import { useEffect, useState, useRef } from 'react';
import { useLocale } from '../../../store/i18nMessage';
import { TableSorter, SortColumn } from './Sorter';
import { TableItem } from './Item';
import { Empty, Spin } from '@bfe/moly';
import { QueryTokenListParams, TokenListItem, queryTokenList } from '../api';
import { useToastFn } from '../../../hooks';
import { safeParseEvmAddress } from '@web3/utils';
import clx from 'classnames';

import styles from './index.module.scss';

interface Props {
  searchText: string;
  chainId: string;
  onChangeHistory: (history: string[]) => void;
}

export const SearchContent = (props: Props) => {
  const { searchText, chainId, onChangeHistory } = props;
  const [sort, setSort] = useState<SortColumn>({});
  const [tokenList, setTokenList] = useState<TokenListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const uid = useRef(0);
  const { t } = useLocale();
  const { warn } = useToastFn();

  const columns = [
    {
      column: 'token',
      title: t('token'),
    },
    {
      column: 'volume',
      title: t('volume'),
    },
    {
      column: 'liquidity',
      title: 'LP',
    },
    {
      column: 'price',
      title: t('price'),
    },
    {
      column: 'change',
      title: '24H %',
    },
  ];

  const changeSort = (column: QueryTokenListParams['orderBy']) => {
    if (!sort || sort?.column !== column) {
      setSort({
        column,
        order: 'desc',
      });
    } else if (sort.order === 'desc') {
      setSort({
        column,
        order: 'asc',
      });
    } else {
      setSort({});
    }
  };

  const sortUitDom = (col: { column: string; title: string }, showDivid: boolean) => {
    return (
      <div className={styles.sort} key={col.column}>
        <TableSorter column={col.column} currentSort={sort} onClick={() => changeSort(col.column as 'price')}>
          <span className={styles.title}>{col.title}</span>
        </TableSorter>
        {showDivid && <div className={styles.divider}></div>}
      </div>
    );
  };

  const fetchTokenList = async () => {
    const params: QueryTokenListParams = {
      tag: 'top',
      limit: 20,
    };

    if (searchText) params.content = searchText;
    if (sort.column) params.orderBy = sort.column;
    if (sort.order) params.direction = sort.order;
    if (chainId) params.chainId = chainId;
    uid.current = uid.current + 1;
    const selfUid = uid.current;
    setIsLoading(true);
    const { result, success } = await queryTokenList({
      ...params,
      content: safeParseEvmAddress(params?.content),
      tag: 'hot',
    });

    if (selfUid !== uid.current) return;

    setIsLoading(false);

    if (!success) {
      warn('systemError');
    }
    setTokenList(result);
  };

  useEffect(() => {
    fetchTokenList();
  }, [searchText, sort, chainId]);

  return (
    <div className={styles.container}>
      <div className={styles.head}>
        <div className={styles.leftSort}>{columns.slice(0, 3).map((col, i, array) => sortUitDom(col, i !== array.length - 1))}</div>
        <div className={styles.rightSort}>{columns.slice(3).map((col, i, array) => sortUitDom(col, i !== array.length - 1))}</div>
      </div>
      <div className={styles.content}>
        {tokenList.map((item) => (
          <TableItem
            searchText={searchText}
            tokenItem={item}
            key={[item.tokenId, item.chainId, item.address].join('-')}
            onChangeHistory={onChangeHistory}
          />
        ))}
        {isLoading && (
          <div className={clx('absolute top-0 bottom-0 right-0 left-0', styles.spin)}>
            <div className="w-full flex justify-center mt-20">
              <Spin theme="dark" />
            </div>
          </div>
        )}
        {!isLoading && tokenList.length === 0 && <Empty title={t('no_data')} />}
      </div>
    </div>
  );
};
